<script lang="ts">
  import Button from '$components/elements/Button.svelte';
  import Icon from '$components/elements/Icon.svelte';
  import Input from '$components/elements/Input.svelte';
  import Modal from '$components/elements/Modal.svelte';
  import Divider from '$components/elements/modal/Divider.svelte';
  import Flex from '$components/layout/Flex.svelte';
  import Desktop from '$components/utils/Desktop.svelte';
  import Mobile from '$components/utils/Mobile.svelte';
  import Text from '$components/utils/Text.svelte';
  import {
    promptLogin,
    readerSettings,
    showDashboard,
    showEmailBlocker,
    showLogin,
    showPledge
  } from '$lib/stores';

  import { _ } from 'svelte-i18n';
  import { login } from '$lib/client/pocketbase/users';
  import { currentUser, pbClient } from '$lib/client/pocketbase';

  let email: string;
  let password: string;
  let wrongCredentials = false;
  let forgottenPassword = false;
  let loading = false;

  function onClose() {
    showLogin.set(false);
    forgottenPassword = false;
    wrongCredentials = false;
    if ($promptLogin === 'confirm') {
      showPledge.set(true);
    }
    promptLogin.set(false);
  }

  const submitLogin = async () => {
    try {
      loading = true;
      await login(email, password);
      onClose();
      if (pbClient.authStore.isValid && $currentUser) {
        readerSettings.set({ ...$currentUser.settings });
        if (!$currentUser.verified) {
          showEmailBlocker.set({ type: 'verification', email: $currentUser.email });
        }
      }
      email = '';
      password = '';
    } catch (e) {
      wrongCredentials = true;
    }
    loading = false;
  };

  $: if (pbClient.authStore.isValid && $showLogin) {
    onClose();
    if ($currentUser && !$currentUser.verified) {
      showEmailBlocker.set({ type: 'verification', email: $currentUser.email });
    } else {
      showDashboard.set(true);
    }
  }
</script>

<Modal bind:showModal={$showLogin} {onClose}>
  <svelte:fragment slot="header">
    <Desktop>
      <Flex
        width="calc(100% - 70px)"
        justifyContent={forgottenPassword ? 'space-between' : 'flex-end'}
        alignItems="center"
        margin={{ top: '20px', left: '50px', right: '20px' }}
      >
        {#if forgottenPassword}
          <Icon
            icon="arrow-left"
            viewBox={15}
            on:click={() => (forgottenPassword = false)}
            cursor="pointer"
          />
        {/if}
        <Icon icon="cross" on:click={onClose} cursor="pointer" casing="30px" />
      </Flex>
    </Desktop>
  </svelte:fragment>
  <div class="login">
    {#if forgottenPassword}
      <Flex gap="12px" direction="column" alignItems="start" margin={{ bottom: '40px' }}>
        <h1 class="login__heading"><Text key="component.login.forgotten.heading" /></h1>
        <p class="login__text">
          <Text key="component.login.forgotten.caption" />
        </p>
      </Flex>
      <form
        class="login__form"
        on:submit|preventDefault={async () => {
          pbClient.collection('users').requestPasswordReset(email);
        }}
      >
        <Flex width="100%" gap="40px" direction="column" alignItems="start">
          <Input
            name="mail"
            type="email"
            label={$_('component.login.input.email')}
            required
            bind:value={email}
          />
          <Button width="100%" submit>
            <Text key="component.login.forgotten.button" />
          </Button>
        </Flex>
      </form>
    {:else}
      <Flex
        gap="12px"
        direction="column"
        margin={{ bottom: '40px' }}
        breakpoints={{
          0: { alignItems: 'center' },
          768: { alignItems: 'start' }
        }}
      >
        <h1 class="login__heading">
          <Text key="component.login.login.heading.1" />
          <Mobile><br /></Mobile>
          <Text key="component.login.login.heading.2" />
        </h1>
        <p class="login__text">
          {#if $promptLogin === 'confirm'}
            Ověření proběhlo v pořádku. Nyní se můžete přihlásit pro zobrazení údajů k platbě.
          {:else if $promptLogin === 'settings'}
            Pro uložení nastavení se musíte přihlásit
          {:else}
            <Text key="component.login.login.caption" />
          {/if}
        </p>
      </Flex>
      <div class="login__form">
        <form on:submit|preventDefault={submitLogin}>
          <Flex width="100%" gap="40px" direction="column" alignItems="start">
            <Input
              name="email"
              type="email"
              label={$_('component.login.input.email')}
              bind:value={email}
              required
            />
            <Input
              name="password"
              type="password"
              label={$_('component.login.input.password')}
              required
              error={wrongCredentials ? $_('component.login.input.wrong-credentials') : undefined}
              bind:value={password}
            />
            <Button width="100%" submit {loading}>
              <Text key="component.login.login.button" />
            </Button>
          </Flex>
        </form>
      </div>
      <Flex
        direction="column"
        gap="46px"
        alignItems="center"
        width="100%"
        breakpoints={{
          0: {
            margin: {
              bottom: '20px',
              top: '35px'
            }
          },
          720: {
            margin: {
              top: '35px'
            }
          }
        }}
      >
        <button
          class="login__link"
          on:click={() => {
            forgottenPassword = true;
          }}
          on:keypress={() => {
            forgottenPassword = true;
          }}
        >
          <Text key="component.login.login.forgotten" />
        </button>
        <Divider />
        <p class="login__text">
          <Text key="component.login.login.register.heading" />
          <Desktop>
            <a class="login__link" href="/klub-bible21/registrace"
              ><Text key="component.login.login.register.button" /></a
            >
          </Desktop>
        </p>
      </Flex>
      <Mobile>
        <Button href="/klub-bible21/registrace" width="100%" color="#C99E77"
          ><Text key="component.login.login.register.button" /></Button
        >
      </Mobile>
    {/if}
  </div>
</Modal>

<style lang="scss">
  .login {
    button {
      appearance: none;
      background: none;
      border: none;
      cursor: pointer;
      font: inherit;
      padding: 0;
    }
    padding: 22px 50px 68px;
    &__heading {
      font-size: 28px;
      line-height: 32px;
    }
    &__text {
      color: rgba($color-dark-gray, 0.6);
      line-height: 30px;
      .login {
        &__link {
          margin: 0 8px;
        }
      }
    }
    &__link {
      color: $color-dark-gray;
      text-decoration: underline;
      font-weight: bold;
      cursor: pointer;
    }
    &__form {
      width: 100%;
    }
  }
  @include mobile {
    .login {
      padding: 22px 26px 41px;
      &__heading {
        text-align: center;
        line-height: 1.2;
      }
    }
  }
</style>
